
 
/* eslint-disable react/prop-types */
import { useMemo,  useState,  useEffect } from "react";
import { pie, arc } from "d3";
import { PieChartContainer } from "./PieChart.elements";
import { CenterValues } from "./CenterValues";
import * as defaultHandlers from "../util/defaultHandlers"
 
 
import { getGroupedSummaryData  } from "../util/getSummaryData";
 
 
 
 
export interface PieChartProps {
  grouping_key?: string;
  colors?: Record<string, string>;
  color?: string;
  value_key?: string;
  totals_key?: string;
  count_key?: string;
  label_key?: string;
  map_keys?: Record<string, string>;
  setSelected?: (value?: Record<string, unknown> | undefined) => void;
  checkIfSelected?: (x?: Record<string, unknown>) => boolean | undefined;
  checkIfFiltered?: (x?: Record<string, unknown>) => boolean | undefined;
  handleClick?: (x?: Record<string, unknown>) => void;
  selected?: Record<string, unknown>;
  [key: string]: unknown;
}

const generatePieChartSettings = (props?: PieChartProps ):PieChartProps | undefined => {
 
  
  return props
}
export const PieChart = (in_props: PieChartProps): JSX.Element => {
  let {
  checkIfSelected,
  handleClick,
  ...props
} = in_props || {}

  const {
    tooltip_ref,
    map_keys,
    size = .8,
    donut = true,
    data_type = 'inline',
    width = 150,
    height = 150,
    colors,
    data,
    grouping_key,
    value_key,
     color_key,
    totals_key,
     count_key,
    toggleTooltip,
   } = (generatePieChartSettings(props) || {}) as PieChartProps
  
 
  let chart_data = data
 

  if (data_type !== 'summary') {
    chart_data = getGroupedSummaryData(
      data,
      {
        totals_key,
        count_key,
        color_key,
        colors,
        grouping_key,
        value_key
      }
    )
  }

 
  const [hoveredIndex, setHoveredIndex] = useState<string | null>(null);
  const internal_select_state = useState<Record<string, unknown> | undefined>(undefined)
 
  let setSelected = props?.setSelected as PieChartProps['setSelected']
  let selected = props?.selected as PieChartProps['selected']
  // use internal selector state if one is not provided in props
  if (!props?.setSelected) [selected, setSelected] = internal_select_state


  // get the keys to use for selection based on the type of item being hovered (bar, legend item, etc)
  // move the default handlers to their own file
  checkIfSelected =  checkIfSelected || ((x?: Record<string, unknown>) => defaultHandlers.checkIfSelected(x, selected)) as PieChartProps['checkIfSelected']
  handleClick = handleClick || ((x?: Record<string, unknown>) => defaultHandlers.handleClick(x, [selected, setSelected])) as PieChartProps['handleClick']
 
  useEffect(() => {
    if (!props?.setSelected) setSelected?.(selected as Record<string, unknown>)
  }, [selected])
 
  const chartSize = Math.min(width, height) 
  const areaPadding = chartSize * .08

  const pathData = useMemo(() => {

    if (!width || !height || !chart_data?.length || !value_key) return [];

    const radius = (chartSize - areaPadding) / 2;
    const innerRadius = !donut ? 0 : (radius * size);

    // Create pie generator
    const pieToUse = pie().value(d => d[value_key])

    // Generate pie data and paths
    return pieToUse(chart_data).map((d, i) => {
      // Create two arc generators - normal and hovered
      const normalArc = arc()
        .innerRadius(innerRadius)
        .outerRadius(radius);
      
      const hoveredArc = arc()
        .innerRadius((innerRadius - ((areaPadding * .2) / 2) ))
        .outerRadius((radius + ((areaPadding * .4) / 2) )); // Thicker on hover

      const isHovered = hoveredIndex === String(i);
      const arcToUse = isHovered ? hoveredArc : normalArc;

      const color_key = String(d.data?.[grouping_key as string])
      const color = (  d.data?.color as string || props?.color || colors?.[color_key] || colors?.[map_keys?.[color_key] as string] || `hsl(${(i * 360) / data.length}, 70%, 50%)`) as string
    
      return {
        large: hoveredArc(d),
        d: arcToUse(d),
        centroid: arcToUse.centroid(d),
        color,
        isHovered,
        grouping_key,
        ...d,
        ...(d.data || {} as Record<string, unknown>),
      };
    });

  }, [chart_data, width, height, grouping_key, value_key, hoveredIndex]);


  const centerX = width / 2
  const centerY = height / 2

  const toggleClassNames = (e, selector = '[data-id="slice"]') => {
    const elements = e.currentTarget.parentElement.querySelectorAll(selector);
    elements.forEach(el => {
      el.classList.remove('is-hovered')
      el.classList.remove('is-not-hovered')
    });
  }
 
  const dataTotal = !value_key ? undefined : chart_data?.reduce((sum, item) => sum + (Number(item[value_key as string]) || 0), 0)

  if (!grouping_key && !value_key) return <div>missing grouping_key or value_key</div>
   
  props = {...props, colors, map_keys, centerX, centerY }

  const center_values = {
    title: selected?.[grouping_key]?.[0] || 'total',
    value: dataTotal,
  }
  
  return (
    <PieChartContainer  className='pie-chart-container chart-area-sizer chart-area-sizer-outer' >
      
    

      <CenterValues {...{...props, ...center_values}}/>
      
      <svg width='100%' height={height} viewBox={`0 0 ${width} ${height}`}>

         
          {pathData.map((item, i) => {
            
            const group = String(item?.[grouping_key as string])
            const count = Number(item?.[count_key as string])
            const itemData = {  item, dataIndex: i, slice: true, group, count, [count_key]: count, [value_key]: item?.[value_key], [grouping_key]: group, grouping_key, value_key, totals_key, color_key, count_key, props }
             
            const isSelected = checkIfSelected?.(itemData)
            let sliceClassName = 'slice'
            if (grouping_key) sliceClassName += ` ${grouping_key}`
            if (isSelected === true) sliceClassName += ' is-selected'
            if (isSelected === false) sliceClassName += ' is-not-selected'

            return (
              <path
                transform={`translate(${centerX}, ${centerY})`}
                className={sliceClassName}
                key={i}
                d={isSelected ? item.large : item.d}
                fill={item.color}
                stroke="white"
                strokeWidth="2"
                data-id="slice"
                data-slice={group}
                data-group={group}
                data-index={i}
                style={{
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  toggleTooltip?.(true)
                  const tooltip_el = tooltip_ref?.current as HTMLDivElement | null

                  if (tooltip_el) {
                    tooltip_el.dataset.group = group
                  }
                  e.currentTarget.parentElement.querySelectorAll('[data-id="slice"]').forEach(el => {
                    if (el !== e.currentTarget) {
                      el.classList.add('is-not-hovered')
                      el.classList.remove('is-hovered')
                    }
                    else {
                      e.currentTarget.classList.add('is-hovered')
                      e.currentTarget.classList.remove('is-not-hovered')
                    }
                  })                  
                  setHoveredIndex(String(i))
                }}
                onMouseLeave={(e) => {
                  toggleTooltip?.(false)
                  toggleClassNames(e)
                  setHoveredIndex(null)
                }}
                 
                onClick={() => {
                  handleClick?.(itemData)
                }}
              />
             
          
          )})}
        
      </svg>
    </PieChartContainer>
  );
}

export default PieChart;